import React, { useRef } from "react";
import { 
  useFieldArray, 
  // useFormState 
} from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import AddLink from "controls/global/add-link";
import { gaps } from "theme/defaultStyle";
import { UWA } from "./UWA";
import { ActionResultStatus, MapActionType, PERMISSIONS, ProductAction, ProductSectionTitle, ProductType, ProductTypeName, ValidationStatus } from "utils/data/enum";

import ConfirmationDialogV3 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV3";
import { UWAProduct } from "entities/UIModel/UWAProduct";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import styled from "styled-components";
import { Box } from "@mui/material";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { useFiles } from "utils/context/FilesContext";
import useCreateFile from "utils/custom-hooks/useCreateFile";
import { ActionResult, SCFile } from "entities/UIModel";
import { getDefaultUwaProduct } from "utils/data/products";
import { useCompanyLocation } from "utils/context/CompanyLocationContext";
// import CompanyStateLocation from "entities/UIModel/company/CompanyStateLocation";
import { runValidation } from "pages/file/utils/yup/validator";
import { isValidUwaPolicyInfo } from "pages/file/utils/uwaHelper";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import mapUiSCFileToApiSCFile from "pages/file/utils/toApi/mapUiSCFileToApiSCFile";
import { showToast } from "controls/global/stewart-toast/showToast";
import { useFilePage } from "utils/context/FilePageContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";

const SUBMIT_SUCCESS = "Underwriting Approval Request successfully submitted!";
const SUBMIT_FAILED = "Underwriting Approval Request submission failed!";
const REVISE_SUCCESS = "Underwriting Approval Request successfully revised!";
const REVISE_FAILED = "Underwriting Approval Request revision failed!";
const CANCEL_SUCCESS = "Underwriting Approval Request cancelled successfully!";
const CANCEL_FAILED = "Underwriting Approval Request cancellation failed!";
const UNDOCANCEL_SUCCESS = "Underwriting Approval Request cancellation successfully undone!";
const UNDOCANCEL_FAILED = "Underwriting Approval Request undo cancellation failed!";


const StyledGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
});

export default function UWACollection() {
  const { fields, append, remove } = useFieldArray({ name: "uwaProducts", keyName: "fieldId" });


  // Permission
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const hasAddUwaRequest = permissionCodes?.includes(PERMISSIONS.UWA_ADDREQUEST);
  
  // Variable for collection
  const { setValue, getValues, clearErrors, trigger } = useFormWrapper();
  // const { errors } = useFormState();
  const uwaProducts: UWAProduct[] = getValues("uwaProducts");
  const [{ isRevisingMode }] = useFilePage();

    
  const [{ companyStateLocations }] = useCompanyLocation();  
  const { reloadFile, resetValidationRequiredFlags, setLocationInfoWithDisplayName } = useCreateFile();      
  // const companyStateLocationsRef = React.useRef<CompanyStateLocation[]>([]);

  // Function Refresh collection's functions
  
  // Constants used during Submit Title Search
  const [{ initialValues }, { submitUwa }] = useFiles();
  const [submitUwaButtonDisabled, setSubmitUwaButtonDisabled] = React.useState<boolean>(false);

  // Constants used during Product Action
  const currentProductAction = React.useRef<string | undefined>("");
  const currentActionResult = React.useRef<ActionResult>({});
  const currentProductIntegrationKey = React.useRef<string | undefined>("");

  // Constants used in Progress dialog 
  const [, { setBypassDefaultLoader }] = useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = React.useState<boolean>(false);
  const [progressMessage, setProgressMessage] = React.useState<string>("");
  const [runProgress, setRunProgress] = React.useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = React.useState<string>("");

  const isFileDisabled = useFieldDisabler("AddUwaButton");


  // Functions used during showing Progress Dialog
  const handleOnCloseProgressDialog = async () => {
    setOpenProgressDialog(false);
    setRunProgress(false);
    setProgressRequestId("");

    // Renable Action button
    switch (currentProductAction.current) {
      case ProductAction.Issue.toString():
        setSubmitUwaButtonDisabled(false);
        break;
    }    
  };

 

  

  // Delete collection item's Functions and variables
  const [, { deleteOrder }] = useFiles();
  const [isDialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const indexToDeleteRef = useRef<number | undefined>();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] = useAutomaticProgressDialogActions();


  const openDeleteDialog = (deleteIndex: number) => {
    indexToDeleteRef.current = deleteIndex;
    setDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    indexToDeleteRef.current = undefined;
    setDialogOpen(false);
  };

  const deleteItem = async () => {
    if (indexToDeleteRef.current === undefined) return;
    const deleteItemIndex = indexToDeleteRef.current;

    if (uwaProducts) {
      remove(deleteItemIndex);

      // Check if the product is saved already
      // If yes, delete from db
      if (uwaProducts.length - 1 >= deleteItemIndex) {
        const deleteOrderId = uwaProducts[deleteItemIndex].orderID;
        if ((deleteOrderId || 0) > 0) {
          setDialogOpen(false);
          openAutomaticProgressDialog();

          await deleteOrder(uwaProducts[deleteItemIndex].orderID);
          await reloadFile();
          closeAutomaticProgressDialog();
        }
      }
    }

    indexToDeleteRef.current = undefined;
    setDialogOpen(false);
  }

  // Add a new UWA
  const setDefaultLocation = (productIndex: number) => {
    if (companyStateLocations.length > 0 && productIndex >= 0) {
      const schema = `uwaProducts.${productIndex}`;
      setLocationInfoWithDisplayName(schema, companyStateLocations[0]);
    }
  }

  const addUwa = () => {
    const newUwa = getDefaultUwaProduct(true);
    newUwa.integrationKey = uuidv4();
    const newItemIndex = uwaProducts.length;
    append(newUwa);
    setValue(`uwaProducts.0.isDefault`, false);
    setValue(`uwaProducts.0.isDirty`, true);

    // setFocusInputElement(`uwaProducts.${newItemIndex}.transactionType`);
    setDefaultLocation(newItemIndex);
  };  

  const handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === "Tab" && !evt.shiftKey && document?.getElementById(ProductTypeName.UWARequest)) {
      evt.preventDefault();
      document?.getElementById(ProductTypeName.UWARequest)?.focus();
    }
  }


  // Submit's functions
  async function handleSubmitUwa (
    uwaProduct: UWAProduct,
    integrationKey: string,
    productAction?: string,
  ): Promise<ActionResult | undefined>  {

    currentProductIntegrationKey.current = integrationKey;
    currentProductAction.current = productAction;

    // Clear previous action error
    clearErrors();
    resetValidationRequiredFlags();

    // -- set validation Underwriter required flag
    const uwaProducts: UWAProduct[] = getValues("uwaProducts");
    uwaProducts?.forEach((p, index) => {
      if (p.integrationKey === integrationKey) {
        if (productAction && [ProductAction.Issue.toString(), ProductAction.Revise.toString() ].includes(productAction)) {
          setValue(`uwaProducts.${index}.isValidationRequired`, true);

          p.uwaPolicyInfos
            ?.forEach((pi, piIndex) => {
              if (isValidUwaPolicyInfo(pi)) {
                setValue(`uwaProducts.${index}.uwaPolicyInfos.${piIndex}.isValidationRequired`, true);
              }
              else {
                setValue(`uwaProducts.${index}.uwaPolicyInfos.${piIndex}.isValidationRequired`, false);
              }
          });
        }
        else {
          setValue(`uwaProducts.${index}.isValidationRequired`, false);

          p.uwaPolicyInfos
            ?.forEach((pi, piIndex) => {
              setValue(`uwaProducts.${index}.uwaPolicyInfos.${piIndex}.isValidationRequired`, false);
          });          
        }

      }
    });

    // setValidLocations(companyStateLocationsRef.current);

    // Call validation
    const currentValues: SCFile = getValues();
    const isValid = await runValidation({
      values: currentValues,
      trigger,
      setValue,
      productType: ProductType.UWARequest,
    });

    if (!isValid) {
      currentActionResult.current.validationStatus = ValidationStatus.InValid;
      return currentActionResult.current;
    } 

    currentActionResult.current.validationStatus = ValidationStatus.Valid

    // Disaable Action button
    switch (productAction) {
      case ProductAction.Issue.toString():
        setSubmitUwaButtonDisabled(true);
        break;
      // TODO - added case here for more action
    }

    // Submit UWA 
    const apiSCFile = mapUiSCFileToApiSCFile(
      initialValues,
      currentValues,
      companyStateLocations, // undefined,
      MapActionType.ProductAction,
      productAction,
      ProductType.UWARequest,
      [integrationKey], //[uwaProduct.integrationKey]
    );    

    // Preparey Message that will be used in both Progress dialog and Toast messages
    const toastTitle = {
      success: '',
      error: '',
      progressBarDialogTitle: 'Please wait while submitting your UW Approval Request...',
    };

    switch (productAction) {
      case ProductAction.Issue:
        toastTitle.success = SUBMIT_SUCCESS;
        toastTitle.error = SUBMIT_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while submitting your Underwriting Approval Request...";
        break;
      case ProductAction.Revise:
        toastTitle.success = REVISE_SUCCESS;
        toastTitle.error = REVISE_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while revising your Underwriting Approval Request...";
        break;
      case ProductAction.Void:
        toastTitle.success = CANCEL_SUCCESS;
        toastTitle.error = CANCEL_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while cancelling your Underwriting Approval Request...";
        break;
      case ProductAction.UndoVoid:
        toastTitle.success = UNDOCANCEL_SUCCESS;
        toastTitle.error = UNDOCANCEL_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while undo cancelling your Underwriting Approval Request...";
        break;
    }    

    // -- show progress dialog
    let progressRequestId = uuidv4();
    setProgressRequestId(progressRequestId);
    setProgressMessage(toastTitle?.progressBarDialogTitle);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);

    // -- calling issue cpl
    const submit = async () => {
      currentActionResult.current = await submitUwa(
        apiSCFile,
        progressRequestId,
        productAction,
        integrationKey
      );

      if (currentActionResult.current?.status === ActionResultStatus.Failed) {
        showToast({
          type: "error",
          title: toastTitle.error,
        });

      } 
      else {
        showToast({
          type: "success",
          title: toastTitle.success,
        });
      }

    };
    
    // Submit/Revise/Void the request 
    await submit();

    // Return action resutl
    return currentActionResult.current;    
  }

  React.useEffect(() => {
    // companyStateLocationsRef.current = companyStateLocations;
    if (companyStateLocations.length > 0 && uwaProducts.length === 1 ) {
      setDefaultLocation(0)
    }
  }, [companyStateLocations]);  

  React.useEffect(() => {
    if(uwaProducts.length > 0) return;
    const newUwaProduct = getDefaultUwaProduct();
    append(newUwaProduct);
    setValue(`uwaProducts.0`, newUwaProduct);
  },[uwaProducts.length])

  // console.log(currentActionResult?.current, "-currentActionResult");
  // console.log("errors:", errors);

  return (
    <>
      <StyledGroup>
        {fields.map((field: any, index: number) => {
          return (
            <UWA
              // key={field.id} // {field.fieldId}
              key={field.integrationKey}
              uwaProduct={field}
              productIndex={index}
              onDelete={() => openDeleteDialog(index)}
              submitButtonDisabled={submitUwaButtonDisabled || isFileDisabled}
              onSubmit={async (integrationKey: string, productAction?: string) => {
                return await handleSubmitUwa(field, integrationKey, productAction);
              }}

            />
          )
        })}
        <Box>
          <AddLink
            onClick={addUwa}
            onKeyDown={handleKeyDown}
            disabled={isRevisingMode || !hasAddUwaRequest || isFileDisabled}
          >
            {ProductTypeName.UWARequest}
          </AddLink>
        </Box>        
      </StyledGroup>
      <ConfirmationDialogV3
        confirmationMessage={`Delete this pending ${ProductSectionTitle.UWARequest}?`}
        isOpen={isDialogOpen}
        onYes={deleteItem}
        onNo={closeDeleteDialog}
        onCancel={closeDeleteDialog}
        cancelActionButton={true}
        singleActionButtonText={"Delete"}
        buttonNoText={"Cancel"}
      />
      
      <ConfirmationDialogWithProgressbar
        autoClose={true}
        title={progressMessage}
        closeButtonText="Done! Click to Continue"
        isOpen={openProgressDialog}
        onClose={handleOnCloseProgressDialog}
        requestId={progressRequestId}
        runProgress={runProgress}
      />      
    </>
  )
}
